import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../services/api";
import SectionCard from "../../components/reporting/SectionCard";
import ActivityProgressBar from "../../components/reporting/ActivityProgressBar";
import ImageGallery from "../../components/reporting/ImageGallery";
import CurrentProjectCard from "../../components/reporting/CurrentProjectCard";
import toast from "react-hot-toast";

/* ✅ Loader (pulsing HomzReady logo) */
import HomzReadyPulseLoader from "../../components/homzready/HomzReadyPulseLoader.jsx";

// ---------- helpers ----------
function computeOverall(activities = []) {
  if (!Array.isArray(activities) || activities.length === 0) return 0;
  const sum = activities.reduce((acc, act) => acc + (Number(act?.progressPercent) || 0), 0);
  return Math.round(sum / activities.length);
}

function normalizeProject(p) {
  if (!p || typeof p !== "object") return null;
  const arr = (v) => (Array.isArray(v) ? v : []);

  const toPics = (list) =>
    arr(list)
      .map((x) => {
        if (!x) return null;
        if (typeof x === "string") return { imageUrl: x };
        const imageUrl = x.imageUrl ?? x.url ?? null;
        if (!imageUrl) return null;
        return { imageUrl, area: x.area ?? x.areaName ?? undefined };
      })
      .filter(Boolean);

  const schedule = p.scheduleData || {};
  const progress = p.progressData || {};

  const toSubActivityGroups = (src) => {
    const list = arr(src);
    if (list.length === 0) return [];
    if (list[0] && (list[0].subActivities || list[0].activity)) {
      return list.map((g) => ({
        activity: g?.activity ?? "",
        subActivities: arr(g?.subActivities).map((s) => ({
          name: s?.name ?? "",
          progressPercent: Number(s?.progressPercent) || 0,
        })),
      }));
    }
    const map = new Map();
    for (const it of list) {
      const activity = (it?.relatedActivity ?? it?.activity ?? "").toString();
      const name = (it?.subActivity ?? it?.name ?? "").toString();
      const progressPercent = Number(it?.progressPercent) || 0;
      if (!map.has(activity)) map.set(activity, []);
      map.get(activity).push({ name, progressPercent });
    }
    return Array.from(map.entries()).map(([activity, subActivities]) => ({
      activity,
      subActivities,
    }));
  };

  return {
    id: p.id,
    related_user_id: p.related_user_id,
    related_user_name: p.related_user_name || p.related_user || p.username,
    name: p.project_name || p.name,
    project_id: p.project_id,
    location: p.location,
    serviceProvider: p.service_provider || p.serviceProvider,
    serviceType: p.service_type || p.serviceType,
    propertyType: p.property_type || p.propertyType,
    areaDefinitions: arr(p.areas),

    scheduleData: {
      startDate: schedule.startDate ?? schedule.start_date ?? null,
      finishDate: schedule.finishDate ?? schedule.finish_date ?? null,
      projectDuration:
        schedule.projectDuration ??
        schedule.projectDurationDays ??
        schedule.project_duration ??
        null,
    },

    progressData: {
      actualStartDate: progress.actualStartDate ?? progress.actual_start_date ?? null,
      expectedFinishDate: progress.expectedFinishDate ?? progress.expected_finish_date ?? null,
      expectedDuration:
        progress.expectedDuration ??
        progress.expectedDurationDays ??
        progress.expected_duration ??
        null,
      remainingDuration:
        progress.remainingDuration ??
        progress.remainingDurationDays ??
        progress.remaining_duration ??
        null,
    },

    activitiesProgress: arr(p.activitiesProgress),
    subActivitiesProgress: toSubActivityGroups(p.subActivitiesProgress),
    agreedDesignPics: toPics(p.agreedDesignPics),
    actualStatusPics: toPics(p.actualStatusPics),
    followUpComments: arr(p.followUpComments),
  };
}

export default function HomzReadyProjectPage() {
  const { username, projectName } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  // all projects for the displayed user (names only)
  const [userProjects, setUserProjects] = useState([]);
  const selectedName = useMemo(() => project?.name ?? projectName, [project, projectName]);

  // 1) Load current project
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/homzready/projects/public/${username}/${projectName}`);
        if (!alive) return;
        setProject(normalizeProject(res.data));
      } catch (err) {
        console.error("Failed to load project", err);
        toast.error("Unable to load project details.");
        if (!alive) return;
        setProject(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [username, projectName]);

  // 2) Load all projects for this user (for dropdown)
  useEffect(() => {
    let alive = true;
    const loadUserProjects = async () => {
      if (!project?.related_user_id && !username) return;

      try {
        const res = await axios.get(`/homzready/projects`, {
          params: { user_id: project.related_user_id },
        });
        if (!alive) return;
        const names = (Array.isArray(res.data) ? res.data : [])
          .map((p) => p.project_name || p.name)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
        setUserProjects(names.length ? names : [selectedName].filter(Boolean));
      } catch {
        if (!alive) return;
        setUserProjects([selectedName].filter(Boolean));
      }
    };

    loadUserProjects();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.related_user_id, username, selectedName]);

  const handleProjectChange = (e) => {
    const nextName = e.target.value;
    if (!nextName) return;
    navigate(`/hmzrdy/${encodeURIComponent(username)}/${encodeURIComponent(nextName)}`);
  };

  /* ✅ Use centered loader while fetching */
  if (loading) return <HomzReadyPulseLoader label="Loading project…" />;

  if (!project)
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6 text-gray-700">
        Project not found.
      </div>
    );

  const activities = Array.isArray(project.activitiesProgress) ? project.activitiesProgress : [];
  const subGroups = Array.isArray(project.subActivitiesProgress) ? project.subActivitiesProgress : [];
  const overallPercent = computeOverall(activities);

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50">
      <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6 max-w-7xl mx-auto">
        {/* Header line: User name + project selector */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="text-2xl font-bold text-gray-800">
            {project.related_user_name}
          </h1>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Project:</label>
            <select
              value={selectedName || ""}
              onChange={handleProjectChange}
              className="border rounded-md px-3 py-2 bg-white text-sm"
            >
              {userProjects.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <CurrentProjectCard project={project} overall={overallPercent} />

        <SectionCard title="Activities Progress">
          {activities.length > 0 ? (
            activities.map((a, i) => (
              <ActivityProgressBar
                key={`${a.activity}-${i}`}
                label={a.activity}
                percent={Number(a.progressPercent) || 0}
                areas={Array.isArray(a.areas) ? a.areas : undefined}
              />
            ))
          ) : (
            <p className="text-sm italic text-gray-500">No activities available.</p>
          )}
        </SectionCard>

        {subGroups.length > 0 && (
          <SectionCard title="Sub-Activities Progress">
            {subGroups.map((group, idx) => (
              <div key={`${group.activity}-${idx}`} className="mb-4">
                <div className="text-sm font-semibold text-gray-800 mb-2">{group.activity}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(Array.isArray(group.subActivities) ? group.subActivities : []).map((sub, j) => (
                    <ActivityProgressBar
                      key={`${group.activity}-${sub.name}-${j}`}
                      label={sub.name}
                      percent={Number(sub.progressPercent) || 0}
                    />
                  ))}
                </div>
              </div>
            ))}
          </SectionCard>
        )}

        {Array.isArray(project.agreedDesignPics) && project.agreedDesignPics.length > 0 && (
          <SectionCard title="Agreed Interior Design">
            <ImageGallery images={project.agreedDesignPics} areas={project.areaDefinitions || []} />
          </SectionCard>
        )}

        {Array.isArray(project.actualStatusPics) && project.actualStatusPics.length > 0 && (
          <SectionCard title="Actual Status Photos">
            <ImageGallery images={project.actualStatusPics} areas={project.areaDefinitions || []} />
          </SectionCard>
        )}

        {Array.isArray(project.followUpComments) && project.followUpComments.length > 0 && (
          <SectionCard title="Following Up Comments">
            <ul className="space-y-2">
              {project.followUpComments.map((c, i) => (
                <li key={`${c.case}-${i}`} className="text-sm">
                  <span className="font-semibold text-gray-800">{c.case}:</span>{" "}
                  <span className="text-gray-700">{c.comment}</span>
                </li>
              ))}
            </ul>
          </SectionCard>
        )}
      </div>
    </div>
  );
}
